// services/chatStream/chatStreamDeps.ts
import { store } from '../../store/store';
import { logout } from '../../slices/auth/authSlice';
import { clientUpdateRequiredReceived } from '../../slices/clientUpdate/clientUpdateSlice';
import { isLegacyExchangePending } from '../legacyExchange';
import { sendToTwoFactorSetup } from '../axiosCreator';
import { getBackendUrl } from '../../config/backendUrl';
import { CHAT_STREAM_PROTOCOL_VERSION } from '../../types/chatStream';
import type { ChatStreamDeps } from './chatStreamClient';
import { withPlainOraViewTurnFlag } from '../../mcgrawHill/plainOraView'; // mcgraw-hill

/**
 * Where the chat turn route lives. Normally the backend; in development it can
 * be pointed at the fixture server (`pnpm chat:fixtures`) with
 * `VITE_CHAT_FIXTURE_URL`, so the chat UI can be built and demoed before the
 * v5 route exists.
 */
export const getChatStreamBaseUrl = (): string => {
  const fixtureUrl = import.meta.env.VITE_CHAT_FIXTURE_URL;
  if (import.meta.env.DEV && typeof fixtureUrl === 'string' && fixtureUrl) {
    return fixtureUrl;
  }

  return getBackendUrl();
};

/**
 * The backend no longer accepts the session cookie (a 401 on a raw fetch):
 * sign the tab out with the same guards the axios client applies.
 */
export const signOutOnLostSession = (): void => {
  const { auth, clientUpdate } = store.getState();
  if (
    auth.isAuthenticated &&
    !clientUpdate.requirement &&
    !isLegacyExchangePending()
  ) {
    store.dispatch(logout());
  }
};

/** Production wiring for the stream client. Tests inject their own deps. */
export const createChatStreamDeps = (): ChatStreamDeps => ({
  baseUrl: getChatStreamBaseUrl(),
  // mcgraw-hill (ORA-1268): a member's plain Ora view asks for plain Ora.
  fetchImpl: (input, init) =>
    fetch(input, {
      ...init,
      body: withPlainOraViewTurnFlag(
        init?.body,
        store.getState().user?.profile
      ),
    }),
  onUnauthorized: signOutOnLostSession,
  onTwoFactorSetupRequired: sendToTwoFactorSetup,
  onClientUpdateRequired: (requirement) => {
    store.dispatch(clientUpdateRequiredReceived(requirement));
  },
  protocolVersion: CHAT_STREAM_PROTOCOL_VERSION,
});
